import {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import classNames from "classnames";
import * as Api from "utils/api/api";
import {DETAILS_VIEW_TYPE} from "utils/constants";
import {handleError} from "utils/errorHandle.function";
import * as reduxSelectors from "components/store/application.reducers";
import {Notification, NotificationType} from "components/common/Notification.component";
import {PATH_TO_DOCUMENT_OVERVIEW_DETAILS} from "components/router/paths";
import {updateDocumentKey} from "components/documentoverview/documentOverview.action";
import {
    getTaskDocumentData,
    hasEditedDocument,
    iframeHasVBNXTFlag,
    integrationIframeHasUnsavedChanges,
    refreshTaskDetails,
    updateDocumentVersion
} from "components/taskdetails-remake/taskDetails.action";
import {
    BNXT_LOG_PREFIX,
    BnxtEditorTarget,
    BnxtEditRequest,
    buildOpenTaskMessage,
    isEditorReadyMessage,
    OPEN_TASK_SUCCESS,
    parseEditorUrl,
    readOpenTaskResult
} from "./bnxtOpenTask";
import type {BnxtDocumentEditorProps, EditorMessageData, EditorUserConfiguration} from "types/editor";
import {postToIframe, registerEditorCommandSender} from "../editorCommands";
import {BnxtEditorSkeleton} from "./BnxtEditorSkeleton";

// budget for each leg of the handshake: fetching the editor url, the editor announcing itself, and then opening the task
export const HANDSHAKE_TIMEOUT_MS = 15000;

// diagnostics only, so never the values - a message on this origin can carry the session
const messageShape = (data: unknown) => data !== null && typeof data === "object" ? Object.keys(data) : typeof data;

const keyOf = ({taskId, companyId, viewType}: Pick<BnxtDocumentEditorProps, "taskId" | "companyId" | "viewType">) =>
    `${taskId}|${companyId}|${viewType}`;

const preventWindowClose = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = true;
    return "";
};

interface BnxtSession {
    target: BnxtEditorTarget;
    editRequest: BnxtEditRequest;
}

export function BnxtDocumentEditor(props: Readonly<BnxtDocumentEditorProps>) {
    const userConfiguration: EditorUserConfiguration = useSelector(reduxSelectors.getUsersData);
    const dispatch = useDispatch();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const sentSessionRef = useRef<BnxtSession | null>(null);
    const [target, setTarget] = useState<BnxtEditorTarget | null>(null);
    // what the current target was fetched for, so a new task, company or view never goes out with the previous token
    const [targetKey, setTargetKey] = useState<string | null>(null);
    const sentRequestIdRef = useRef<string | null>(null);
    // tied to the url so a new editor url is not ready until that document announces itself; a new object per ready
    const [readyState, setReadyState] = useState<{url: string} | null>(null);
    const editorReady = target !== null && readyState?.url === target.url;
    const targetUrlRef = useRef<string | null>(null);
    targetUrlRef.current = target?.url ?? null;
    // each document sends one ready and fires one load in either order, so it has announced itself once readies catch up
    const handshakeCountsRef = useRef({url: null as string | null, loads: 0, readies: 0});
    const [taskOpen, setTaskOpen] = useState(false);
    const [sessionError, setSessionError] = useState(false);

    const {taskId, id, companyId, externalAppId, viewType, isReadOnly, detailsLoaded = true} = props;
    const {currentContext, currentUserId} = userConfiguration;
    const isAdministrator = userConfiguration.userRoles.systemAdministrator;
    const requestedKey = keyOf(props);

    // read through a ref so an inline callback from the parent cannot restart the timer on every render
    const onHandshakeFailedRef = useRef(props.onHandshakeFailed);
    onHandshakeFailedRef.current = props.onHandshakeFailed;
    // the message listener outlives renders, so the editing handlers read the latest props from here
    const propsRef = useRef(props);
    propsRef.current = props;
    const editingRef = useRef(false);
    const documentVersionRef = useRef<number | null>(null);
    // the key openTask went out for, and the one the editor confirmed; document events only count for the latter
    const sentKeyRef = useRef<string | null>(null);
    const openedKeyRef = useRef<string | null>(null);
    // until the current task is open the iframe still shows the previous document, whose edits must not save here
    const isCurrentTaskOpen = () => openedKeyRef.current === keyOf(propsRef.current);

    const stopEditing = (version: number) => {
        const {taskId, uid, companyId, viewType, reapproveType, route} = propsRef.current;
        const skipWorkflowRestart = reapproveType === "CONFIRMATION";

        window.removeEventListener("beforeunload", preventWindowClose);
        // this needs to happen here because of the navigation blocker in task details
        dispatch(integrationIframeHasUnsavedChanges(false));

        if (viewType === DETAILS_VIEW_TYPE.DOCUMENT) {
            Api.updateDocument(taskId, version, skipWorkflowRestart, companyId).then((response: {result: string}) => {
                dispatch(updateDocumentKey(taskId, response.result));
                route(PATH_TO_DOCUMENT_OVERVIEW_DETAILS + response.result, {replace: true});
            }, handleError);
        } else {
            dispatch(updateDocumentVersion(version));
            Api.updateTask(taskId, version, skipWorkflowRestart).then(() => {
                dispatch(refreshTaskDetails(taskId, uid));
                dispatch(getTaskDocumentData(taskId, false));
            }, handleError);
        }
    };

    const handleReadyOrViewingEvent = (version: number | undefined) => {
        const wasEditing = editingRef.current;
        editingRef.current = false;

        if (!propsRef.current.isReadOnly && wasEditing && version && version > (documentVersionRef.current ?? 0)) {
            documentVersionRef.current = version;
            stopEditing(version);
            dispatch(hasEditedDocument());
            return;
        }

        documentVersionRef.current = version ?? null;
        window.removeEventListener("beforeunload", preventWindowClose);
        dispatch(integrationIframeHasUnsavedChanges(false));
    };

    const handleEditingEvent = (supportsSaveAndDiscard: boolean | undefined) => {
        editingRef.current = true;
        dispatch(iframeHasVBNXTFlag(supportsSaveAndDiscard));
        dispatch(integrationIframeHasUnsavedChanges(true));
        window.addEventListener("beforeunload", preventWindowClose);
    };

    useEffect(() => () => window.removeEventListener("beforeunload", preventWindowClose), []);

    useEffect(() => registerEditorCommandSender((message) => postToIframe(iframeRef.current, message)), []);

    const countsFor = (url: string) => {
        if (handshakeCountsRef.current.url !== url) {
            handshakeCountsRef.current = {url, loads: 0, readies: 0};
        }

        return handshakeCountsRef.current;
    };

    // the next document has to be sent the session afresh, and nothing it is answering for may count
    const resetHandshake = () => {
        setTaskOpen(false);
        sentSessionRef.current = null;
        sentRequestIdRef.current = null;
        openedKeyRef.current = null;
    };

    useEffect(() => {
        let cancelled = false;
        const isDocumentView = viewType === DETAILS_VIEW_TYPE.DOCUMENT;
        const getEditor = isDocumentView ? Api.getDocumentOverviewEditor : Api.getDocumentEditor;

        // the previous target stays so the iframe is not reloaded; the next task goes to it over postMessage
        setTaskOpen(false);
        setSessionError(false);
        // a late answer to the previous task's openTask must not count for this one
        sentRequestIdRef.current = null;
        openedKeyRef.current = null;
        editingRef.current = false;
        documentVersionRef.current = null;

        const showError = () => {
            setReadyState(null);
            setSessionError(true);
        };

        // without a target the handshake timer never starts, so a request that never answers needs its own
        const fetchTimeout = window.setTimeout(() => {
            console.warn(`${BNXT_LOG_PREFIX} the editor url did not arrive within ${HANDSHAKE_TIMEOUT_MS}ms`);
            onHandshakeFailedRef.current?.();
        }, HANDSHAKE_TIMEOUT_MS);

        getEditor(taskId, companyId).then((response) => {
            window.clearTimeout(fetchTimeout);

            if (cancelled) {
                return;
            }

            const editorTarget = parseEditorUrl(response?.url);

            if (!editorTarget) {
                showError();
                return;
            }

            // a new url remounts the iframe, so a ready recorded for that url earlier no longer holds
            if (editorTarget.url !== targetUrlRef.current) {
                setReadyState(null);
            }

            setTarget(editorTarget);
            setTargetKey(requestedKey);
        }).catch(() => {
            window.clearTimeout(fetchTimeout);

            if (!cancelled) {
                showError();
            }
        });

        return () => {
            cancelled = true;
            window.clearTimeout(fetchTimeout);
        };
    }, [taskId, companyId, viewType, requestedKey]);

    // a new object here is what makes the openTask effect re-post, so everything the request reads has to be a dependency
    const session: BnxtSession | null = useMemo(() => target === null || targetKey !== requestedKey ? null : {
        target,
        editRequest: {
            companyId: (viewType === DETAILS_VIEW_TYPE.TASK ? companyId : currentContext) + "",
            systemId: externalAppId + "",
            userId: currentUserId + "",
            readonly: isReadOnly,
            administrator: isAdministrator,
            taskId: id
        }
    }, [target, targetKey, requestedKey, id,companyId, externalAppId, viewType, isReadOnly, currentContext, currentUserId,
        isAdministrator]);

    // editorReady is a dependency so that opening the task gets a budget of its own once the editor has answered
    useEffect(() => {
        // openTask is held until the details load, so its budget only starts once it can actually go out
        if (!target || taskOpen || (editorReady && !detailsLoaded)) {
            return;
        }

        const timeout = window.setTimeout(() => {
            console.warn(`${BNXT_LOG_PREFIX} the editor did not ${editorReady ? "open the task" : "report ready"} `
                + `within ${HANDSHAKE_TIMEOUT_MS}ms`, target.url);
            onHandshakeFailedRef.current?.();
        }, HANDSHAKE_TIMEOUT_MS);

        return () => window.clearTimeout(timeout);
    }, [target, editorReady, taskOpen, detailsLoaded]);

    useEffect(() => {
        const onMessage = (event: MessageEvent<EditorMessageData | undefined>) => {
            if (!target || event.origin !== target.origin) {
                return;
            }

            // only the editor iframe itself may drive the handshake; any other window on the origin could spoof a ready
            if (event.source !== iframeRef.current?.contentWindow) {
                console.warn(`${BNXT_LOG_PREFIX} ignored a message from another window on the editor origin`,
                    messageShape(event.data));
                return;
            }

            if (isEditorReadyMessage(event.data)) {
                countsFor(target.url).readies++;

                // edits made before this task was open belong to another task, so only the version is kept
                if (!isCurrentTaskOpen()) {
                    editingRef.current = false;
                }

                handleReadyOrViewingEvent(event.data?.version);

                resetHandshake();
                setReadyState({url: target.url});
                return;
            }

            if (event.data?.event === "viewing" || event.data?.event === "editing") {
                if (!isCurrentTaskOpen()) {
                    console.warn(`${BNXT_LOG_PREFIX} ignored ${event.data.event} before the task was open`);
                } else if (event.data.event === "viewing") {
                    handleReadyOrViewingEvent(event.data.version);
                } else {
                    handleEditingEvent(event.data.supportsSaveAndDiscard);
                }

                return;
            }

            const result = readOpenTaskResult(event.data);

            if (!result) {
                console.warn(`${BNXT_LOG_PREFIX} unrecognised message from the editor`, messageShape(event.data));
                return;
            }

            // one answer per request, so a late one must not tear down an editor that is already open
            if (taskOpen) {
                console.warn(`${BNXT_LOG_PREFIX} ${result.command} after the task was already open`, result.requestId);
                return;
            }

            // a result for an openTask we no longer care about, e.g. a late answer for the previous task
            if (result.requestId !== sentRequestIdRef.current) {
                console.warn(`${BNXT_LOG_PREFIX} ${result.command} for a stale request`, result.requestId);
                return;
            }

            if (result.command === OPEN_TASK_SUCCESS) {
                openedKeyRef.current = sentKeyRef.current;
                setTaskOpen(true);
                return;
            }

            console.warn(`${BNXT_LOG_PREFIX} the editor could not open the task`, result.requestId);
            onHandshakeFailedRef.current?.();
        };

        window.addEventListener("message", onMessage, false);

        return () => window.removeEventListener("message", onMessage);
    }, [target, taskOpen]);

    // readyState is a dependency so that every document that announces itself is sent the session
    useEffect(() => {
        if (!editorReady || !session || !detailsLoaded || sentSessionRef.current === session) {
            return;
        }

        const message = buildOpenTaskMessage(session.target.approvalToken, session.editRequest);

        sentRequestIdRef.current = message.requestId;
        sentKeyRef.current = requestedKey;
        openedKeyRef.current = null;
        setTaskOpen(false);
        iframeRef.current?.contentWindow?.postMessage(message, session.target.origin);
        sentSessionRef.current = session;
    }, [editorReady, readyState, session, detailsLoaded]);

    const onIframeLoad = () => {
        console.log(`${BNXT_LOG_PREFIX} iframe loaded`, target?.url);

        if (!target) {
            return;
        }

        const counts = countsFor(target.url);
        counts.loads++;

        // a document that loaded without having announced itself has to do so before we may post the session to it
        if (counts.loads > counts.readies) {
            setReadyState(null);
            resetHandshake();
        }
    };

    const frameClasses = classNames("external-editor__frame document-editor-frame", {"document-editor-popup": props.isPopup});

    if (sessionError) {
        return (
            <div className="external-editor__surface">
                <div className={frameClasses}>
                    <div className="external-editor__message">
                        <Notification
                            type={NotificationType.ERROR}
                            title={props.translate("popUp.documentEditor.errorTitle")}
                            description={props.translate("popUp.documentEditor.errorSuggestion")}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="external-editor__surface">
            {target &&
                <iframe
                    // a new window per url, so a late ready from the old document cannot pass the source check
                    key={target.url}
                    ref={iframeRef}
                    onLoad={onIframeLoad}
                    id={"Bnxt-Document-Editor-Iframe"}
                    src={target.url}
                    title={"Document Editor"}
                    allow={"clipboard-read; clipboard-write"}
                    className={frameClasses}
                />
            }
            {/* after ready the editor shows its own loader while it opens the task, so ours would overlap it */}
            {!editorReady && <BnxtEditorSkeleton/>}
        </div>
    );
}

export default BnxtDocumentEditor;
